import { GameType } from '@hit-game/shared/enums';
import type { IGroup } from '@hit-game/shared/types';
import { useEffect, useState } from 'react';

import { Button } from '../common/Button';
import { Dropdown } from '../common/Dropdown';
import { FIELD_CLASS, LABEL_CLASS } from '../common/form.styles';
import { Modal } from '../common/Modal';
import { GAME_OPTIONS } from './group.constants';

/** Hộp thoại tạo chỉ hỏi 2 thứ nên hẹp hơn hộp thoại sửa. */
const MODAL_WIDTH = 460;

interface GroupCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Pick<IGroup, 'name' | 'gameType'>) => Promise<boolean>;
}

/**
 * Tạo nhóm mới — CHỈ hỏi tên và game.
 *
 * Tách hẳn khỏi hộp thoại sửa thay vì một component nhận cờ `isEdit`: cờ đó đổi
 * bề rộng, tiêu đề, chữ trên nút, bộ luật kiểm tra, hình dạng payload VÀ gần hết
 * thân form — tức là hai màn khác nhau bị nhét chung một hàm 260 dòng. Nơi gọi
 * cũng đã render hai instance riêng từ trước, nên cờ chưa bao giờ có tác dụng gì.
 */
export function GroupCreateModal({ open, onClose, onSubmit }: GroupCreateModalProps) {
  const [name, setName] = useState('');
  const [gameType, setGameType] = useState<GameType>(GameType.TX);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Nạp lại mỗi lần mở: giữ state cũ thì lần tạo sau hiện tên vừa gõ lần trước.
  useEffect(() => {
    if (!open) return;
    setName('');
    setGameType(GameType.TX);
    setError(null);
  }, [open]);

  const submit = async () => {
    if (!name.trim()) {
      setError('Nhập tên nhóm');
      return;
    }

    setSaving(true);
    const ok = await onSubmit({ name: name.trim(), gameType });
    setSaving(false);

    if (ok) onClose();
    else setError('Lưu không thành công — kiểm tra lại rồi thử lại');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={saving}
      maxWidth={MODAL_WIDTH}
      title="Tạo nhóm mới"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Huỷ
          </Button>
          <Button
            onClick={() => {
              void submit();
            }}
            loading={saving}
          >
            Tạo nhóm
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="group-name">
              Tên nhóm
            </label>
            <input
              id="group-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="VD: Cặp Tài Xỉu 1"
              className={FIELD_CLASS}
            />
          </div>

          <div>
            <span className={LABEL_CLASS}>Game</span>
            <Dropdown
              value={gameType}
              onChange={(value) => {
                setGameType(value as GameType);
              }}
              options={GAME_OPTIONS}
              searchable={false}
            />
          </div>
        </div>

        <p className="text-ink-faint text-[11px]">
          Thành viên và mệnh giá cược đặt sau, ở nút Sửa của nhóm.
        </p>

        {error && <p className="text-danger text-[12px] font-medium">{error}</p>}
      </div>
    </Modal>
  );
}
